import { useState, useEffect } from 'react';
import { fixturesAPI, seriesAPI, divisionsAPI, teamsAPI, groundsAPI } from '../../services/api';
import { PageHeader, SectionTitle, DataTable, StatusBadge, Spinner, LoadingPage, ConfirmDialog } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

export default function SchedulePage() {
  const { user, isAdmin, isScheduler } = useAuthStore();
  const isTeamUser = user?.role === 'team_user';

  const [fixtures, setFixtures] = useState([]);
  const [stats, setStats] = useState({});
  const [series, setSeries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [filters, setFilters] = useState({
    series_id: '', division_id: '', team_id: '', ground_id: '', status: '', date_from: '', date_to: ''
  });
  const [selectedSeries, setSelectedSeries] = useState('');

  useEffect(() => {
    seriesAPI.list().then(r => { setSeries(r.data); if (r.data.length) setSelectedSeries(r.data[0].id); }).catch(() => {});
    teamsAPI.list({}).then(r => setTeams(r.data)).catch(() => {});
    groundsAPI.list().then(r => setGrounds(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (filters.series_id) {
      divisionsAPI.list(filters.series_id).then(r => setDivisions(r.data)).catch(() => {});
    }
  }, [filters.series_id]);

  useEffect(() => { loadFixtures(); }, [filters]);

  const loadFixtures = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (isTeamUser) params.team_id = user.team_id;
      const [fix, st] = await Promise.all([
        fixturesAPI.list(params),
        fixturesAPI.stats(filters.series_id)
      ]);
      setFixtures(fix.data);
      setStats(st.data);
    } catch (err) { toast.error('Failed to load fixtures'); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    if (!filters.series_id) return toast.error('Select a series first');
    setGenerating(true);
    try {
      const { data } = await fixturesAPI.generate(filters.series_id, {});
      toast.success(`Generated! ${data.total_fixtures} fixtures across ${data.divisions.length} divisions.`);
      if (data.conflicts?.length) toast(`⚠ ${data.conflicts.length} conflicts need resolution`, { icon: '⚠' });
      loadFixtures();
    } catch (err) { toast.error(err.response?.data?.error || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleDeleteSeries = async () => {
    try {
      await fixturesAPI.deleteSeries(confirmDelete);
      toast.success('Schedule deleted');
      setConfirmDelete(null);
      loadFixtures();
    } catch (err) { toast.error('Delete failed'); }
  };

  const setFilter = (key, value) => setFilters(p => ({ ...p, [key]: value }));
  const clearFilters = () => setFilters({ series_id: '', division_id: '', team_id: '', ground_id: '', status: '', date_from: '', date_to: '' });

  const statusColor = (s) => ({ confirmed: 'tag-green', conflict: 'tag-gold', unscheduled: 'tag-purple', cancelled: 'tag-red' }[s] || 'tag-blue');

  if (loading && !fixtures.length) return <LoadingPage />;

  return (
    <div>
      <PageHeader icon="▦"
        title={isTeamUser ? 'My Schedule' : 'Schedule'}
        subtitle={isTeamUser ? `Fixtures for ${user?.team_name || 'your team'}` : 'Generate, filter, view and export the full season schedule.'}
        actions={!isTeamUser && (
          <>
            <button className="btn btn-ghost" onClick={() => {
              if (filters.series_id) window.open(fixturesAPI.exportExcelUrl(filters.series_id), '_blank');
              else toast.error('Select a series first');
            }}>📊 Export Excel</button>
            {isScheduler() && (
              <button className="btn btn-gold" onClick={handleGenerate} disabled={generating}>
                {generating ? <><Spinner size={14} /> Generating...</> : '⚡ Generate Schedule'}
              </button>
            )}
          </>
        )}
      />

      {/* Status summary cards */}
      {!isTeamUser && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total Fixtures', value: stats.total || 0, color: 'var(--blue)' },
            { label: 'Confirmed',      value: stats.confirmed || 0, color: 'var(--accent)' },
            { label: 'Conflicts',      value: stats.conflicts || 0, color: 'var(--gold)' },
            { label: 'Unscheduled',    value: stats.unscheduled || 0, color: 'var(--purple)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: 14 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: 'monospace' }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--mid)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 3, height: 14, background: 'var(--blue)', borderRadius: 2 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', letterSpacing: '1px', textTransform: 'uppercase' }}>Filters</span>
          <button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{ marginLeft: 'auto' }}>Clear All</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>Series</label>
            <select className="input select" style={{ fontSize: 12 }} value={filters.series_id} onChange={e => setFilter('series_id', e.target.value)}>
              <option value="">All Series</option>
              {series.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>Division</label>
            <select className="input select" style={{ fontSize: 12 }} value={filters.division_id} onChange={e => setFilter('division_id', e.target.value)}>
              <option value="">All Divisions</option>
              {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>Team</label>
            <select className="input select" style={{ fontSize: 12 }} value={filters.team_id} onChange={e => setFilter('team_id', e.target.value)} disabled={isTeamUser}>
              <option value="">All Teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.short_name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>Ground</label>
            <select className="input select" style={{ fontSize: 12 }} value={filters.ground_id} onChange={e => setFilter('ground_id', e.target.value)}>
              <option value="">All Grounds</option>
              {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>Status</label>
            <select className="input select" style={{ fontSize: 12 }} value={filters.status} onChange={e => setFilter('status', e.target.value)}>
              <option value="">All Statuses</option>
              <option value="confirmed">✅ Confirmed</option>
              <option value="conflict">⚠ Conflict</option>
              <option value="unscheduled">📅 Unscheduled</option>
              <option value="cancelled">❌ Cancelled</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', display: 'block', marginBottom: 4 }}>Date Range</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input className="input" type="date" style={{ fontSize: 11, padding: '6px 8px' }} value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
              <span style={{ color: 'var(--mid)', fontSize: 11, flexShrink: 0 }}>–</span>
              <input className="input" type="date" style={{ fontSize: 11, padding: '6px 8px' }} value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--mid)' }}>
            Showing <strong style={{ color: 'var(--text)' }}>{fixtures.length} matches</strong>
          </span>
          {fixtures.filter(f => f.status === 'conflict').length > 0 && (
            <span className="tag tag-gold">{fixtures.filter(f => f.status === 'conflict').length} conflicts</span>
          )}
        </div>
      </div>

      {/* Fixtures table */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div> : (
          <DataTable
            headers={['#', 'Series', 'Division', 'Date', 'Time', 'Home', 'Away', 'Ground', 'Status', ...(isScheduler() ? ['Actions'] : [])]}
            accentColor="var(--gold)"
            rows={fixtures.map((f, i) => [
              <span className="mono" style={{ color: 'var(--mid)', fontSize: 12 }}>{i + 1}</span>,
              <span style={{ fontSize: 12 }}>{f.series_short || f.series_name}</span>,
              <span style={{ fontSize: 12 }}>{f.division_name || '—'}</span>,
              <span className="mono" style={{ fontSize: 12 }}>{f.match_date ? new Date(f.match_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—'}</span>,
              <span className="mono" style={{ fontSize: 12 }}>{f.match_time ? f.match_time.substring(0, 5) : '—'}</span>,
              <strong style={{ fontSize: 13 }}>{f.home_short}</strong>,
              <span style={{ fontSize: 13 }}>{f.away_short}</span>,
              <span style={{ fontSize: 12 }}>{f.ground_short || f.ground_name || '—'}</span>,
              <span className={`tag ${statusColor(f.status)}`}>{f.status}</span>,
              ...(isScheduler() ? [
                <button className="btn btn-ghost btn-sm" onClick={() => toast('Edit fixture — coming soon')}>Edit</button>
              ] : [])
            ])}
            emptyMessage={loading ? 'Loading...' : 'No fixtures found for the selected filters.'}
          />
        )}
      </div>

      {/* Conflict alerts */}
      {fixtures.filter(f => f.status === 'conflict').length > 0 && (
        <div className="wnote" style={{ marginBottom: 16 }}>
          <strong>⚠ {fixtures.filter(f => f.status === 'conflict').length} conflicts need resolution:</strong>
          {fixtures.filter(f => f.status === 'conflict').map((f, i) => (
            <div key={i} style={{ marginTop: 4, fontSize: 12 }}>
              {f.home_short} vs {f.away_short} — {f.conflict_notes || 'No slot available'}
            </div>
          ))}
        </div>
      )}

      {/* Export options + danger zone (admin only) */}
      {!isTeamUser && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle color="var(--gold)">Export Options</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Excel (Master Format)',  desc: 'Matches Schedule_Master.xlsm layout', icon: '📊', action: () => filters.series_id ? window.open(fixturesAPI.exportExcelUrl(filters.series_id), '_blank') : toast.error('Select a series') },
                { label: 'Push to CricClubs',      desc: 'Upload confirmed fixtures via CricClubs API (Method 1 only)', icon: '⟳', action: () => window.location.href = '/cricclubs' },
              ].map(e => (
                <div key={e.label} onClick={e.action}
                  style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', transition: 'border .15s' }}
                  onMouseEnter={ev => ev.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={ev => ev.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{e.icon} {e.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2 }}>{e.desc}</div>
                </div>
              ))}
            </div>
          </div>
          {isAdmin() && (
            <div className="card" style={{ padding: 16 }}>
              <SectionTitle color="var(--red)">Danger Zone</SectionTitle>
              <p className="note" style={{ marginBottom: 12 }}>Delete all fixtures for the currently selected series. This cannot be undone.</p>
              <button className="btn btn-danger" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => filters.series_id ? setConfirmDelete(filters.series_id) : toast.error('Select a series')}>
                🗑 Delete Series Schedule
              </button>
            </div>
          )}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          danger
          message="This will permanently delete ALL fixtures for this series. This action cannot be undone."
          onConfirm={handleDeleteSeries}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
